import { applyBrandTheme } from '../lib/brandTheme.js';
import { rewardCopy } from '../lib/reward.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { identifyUser } from '../lib/logrocket.js';
import RavePilotHeader from '../components/RavePilotHeader.jsx';
import RavePilotFooter from '../components/RavePilotFooter.jsx';
import Stepper from '../components/Stepper.jsx';
import ScriptRecorder from '../components/ScriptRecorder.jsx';
import ClipPlayerOverlay from '../components/ClipPlayerOverlay.jsx';

// ── Flow stages ────────────────────────────────────────────────────────
// 'loading'      — fetching submission state from API
// 'invalid'      — token bad or submission closed
// 'welcome'      — initial pitch: earnings hero + examples (no stepper here)
// 'pick'         — script selection (pick 3 of 4)
// 'fill'         — fill blanks for the active script
// 'record'       — handed off to <ScriptRecorder>
// 'between'      — "great, on to the next" interstitial
// 'submit'       — confirmation card + quiet inline consent
// 'done'         — redirects to /r/:token/thanks
//
// State persisted to localStorage so a reload picks up where they left off.

export default function Record() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stage, setStage] = useState('loading');
  const [error, setError] = useState(null);

  const [chosenKeys, setChosenKeys] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [currentKey, setCurrentKey] = useState(null);  // the single story being recorded right now
  const [fills, setFills] = useState({});
  // Practice warm-up: a fun, throwaway take before the real scripts. The
  // chosen "fun blank" answer rides along into the practice teleprompter.
  const [practiceFun, setPracticeFun] = useState('out in the garden');
  const [freeformAutoUpload, setFreeformAutoUpload] = useState(false);

  const lsKey = `vt-state-${token}`;
  useEffect(() => {
    try {
      // ?reset=1 — caller wants a fresh session. Wipe the stored state for
      // this token, then strip the query so a reload doesn't loop and the
      // URL stays clean. Useful for testing without an incognito tab.
      const params = new URLSearchParams(location.search);
      if (params.get('reset') === '1') {
        localStorage.removeItem(lsKey);
        params.delete('reset');
        const clean = location.pathname + (params.toString() ? `?${params}` : '');
        history.replaceState(null, '', clean);
        return;   // skip hydration this render — state is already initial values
      }
      const raw = localStorage.getItem(lsKey);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.chosenKeys) setChosenKeys(s.chosenKeys);
        if (s.fills)      setFills(s.fills);
        if (s.activeIdx === 0 || s.activeIdx) setActiveIdx(s.activeIdx);
        if (s.stage && s.stage !== 'record' && s.stage !== 'practiceRecord') {
          setStage(s.stage);
        } else if (s.stage === 'practiceRecord') {
          setStage('practice');   // reload mid-practice → back to the calm intro
        }
      }
    } catch { /* ignore */ }
  }, [lsKey]);

  useEffect(() => {
    if (stage === 'loading') return;
    localStorage.setItem(lsKey, JSON.stringify({ chosenKeys, fills, activeIdx, stage }));
  }, [chosenKeys, fills, activeIdx, stage, lsKey]);

  useEffect(() => {
    (async () => {
      try {
        const d = await api(`/api/submission/${token}`);
        setData(d);
        // Tag the LogRocket session with the customer's identity. Safe to
        // call even if init hasn't completed yet (the helper handles it).
        if (d?.submission?.customer_email) {
          identifyUser({
            email: d.submission.customer_email,
            name: d.submission.customer_name,
            role: 'customer',
          });
        }
        if (d.submission.status === 'submitted' || d.submission.status === 'approved' || d.submission.status === 'paid') {
          setStage('done');
          navigate(`/r/${token}/thanks`, { replace: true });
          return;
        }
        // Drop any localStorage-hydrated chosenKeys that don't exist in the
        // collector anymore. This commonly happens when scripts get renamed
        // or removed between testing sessions — and without this filter,
        // those stale keys make the Fill stage do scripts.find() → undefined
        // → setStage('pick') during render → bounce-back loop that looks
        // like "Continue button doesn't work."
        applyBrandTheme(d.collector && d.collector.brand);
        const validKeys = new Set((d.collector.scripts || []).map((s) => s.key));

        // Backfill chosenKeys with any accepted-clip script_keys that
        // aren't already there. Real condition this catches: a server-side
        // reset cleared submitted_at/consent but preserved the 4 clip rows,
        // while the customer's localStorage chosenKeys is empty (because it
        // was wiped along with the reset, or never set on this device). The
        // accepted clips ARE the source of truth for what the customer
        // recorded -- chosenKeys is just the order hint. Without this
        // backfill, the Between screen's hero thumb has nothing to point at
        // and renders a forever-spinner.
        const acceptedOrderedKeys = (d.clips || [])
          .filter((c) => (c.status === 'uploaded' || c.status === 'approved') && validKeys.has(c.script_key))
          .sort((a, b) => (a.created_at || 0) - (b.created_at || 0))
          .map((c) => c.script_key);

        setChosenKeys((prev) => {
          const filtered = (prev || []).filter((k) => validKeys.has(k));
          const seen = new Set(filtered);
          const seenAccept = new Set();
          const acceptedUnique = acceptedOrderedKeys.filter((k) => {
            if (seenAccept.has(k)) return false;
            seenAccept.add(k); return true;
          });
          const merged = [...filtered];
          for (const k of acceptedUnique) {
            if (!seen.has(k)) { merged.push(k); seen.add(k); }
          }
          return merged;
        });

        const hasAcceptedClips = (d.clips || []).some(
          (c) => c.status === 'uploaded' || c.status === 'approved'
        );
        setStage((s) => (s === 'loading' ? (hasAcceptedClips ? 'pick' : 'welcome') : s));
      } catch (e) {
        setError(e.message || 'Unable to load your invitation.');
        setStage('invalid');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const acceptedScriptKeys = useMemo(
    () => new Set((data?.clips || []).filter((c) => c.status === 'uploaded' || c.status === 'approved').map((c) => c.script_key)),
    [data?.clips]
  );

  // Poll for Stream-encoding completion. When a customer finishes recording,
  // their clip uploads to Cloudflare Stream and starts encoding (~30-90s).
  // The Stream webhook flips stream_ready=1 in our DB once encoding's done,
  // but the Between/Submit screens only know about it if we re-fetch.
  //
  // Without polling, Felix sees the spinner-instead-of-play-button forever
  // and assumes playback is broken. With polling, the play button appears
  // as soon as Stream finishes encoding, naturally.
  //
  // Polls on BOTH between AND submit stages. Customers often click "Send
  // my videos" while a spinner's still spinning on Between -- without
  // polling on submit, the clips stay frozen in the encoding state on the
  // Submit screen and the play buttons there stay disabled. ThankYou has
  // its own polling (it owns its own /api/submission fetch).
  //
  // Pauses entirely when the tab is hidden so a backgrounded phone doesn't
  // burn requests.
  useEffect(() => {
    if (!data) return;
    if (stage !== 'between' && stage !== 'submit') return;
    const notReady = (data.clips || []).some((c) =>
      (c.status === 'uploaded' || c.status === 'approved') && !c.stream_ready
    );
    if (!notReady) return;

    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      if (document.visibilityState === 'hidden') return;   // skip when tab is in bg
      try {
        const d = await api(`/api/submission/${token}`);
        if (!stopped) setData(d);
      } catch { /* ignore — keep polling */ }
    };
    const id = setInterval(tick, 3000);
    return () => { stopped = true; clearInterval(id); };
  }, [data, stage, token]);
  const requiredClips = data?.collector?.required_clips ?? 3;
  const remaining = Math.max(0, requiredClips - acceptedScriptKeys.size);

  // Resolve the active script for the fill/record stages. Computed here at
  // the top of the component (before any early returns) so the hook order
  // never changes between renders — Rules of Hooks. When data is still
  // loading these inputs are empty and activeScript is null; the bounce
  // effect below only fires once stage is fill/record AND we've confirmed
  // no valid script (i.e. stale chosenKeys from a previous session).
  //
  // We CANNOT call setStage during render — React will warn and re-render
  // endlessly, which presents to the user as "Continue button doesn't
  // work." Hence the effect.
  const isInterview = data?.collector?.mode === 'interview';
  const scriptsForRender = isInterview
    ? (data?.collector?.questions || []).map((q) => ({
        key: `q:${q.id}`,
        title: q.text,
        summary: q.hint || 'Watch the question, then answer in your own words.',
        prompts: [],
        question: q,
      }))
    : (data?.collector?.scripts || []);
  const remainingChosen = chosenKeys.filter((k) => !acceptedScriptKeys.has(k));
  const activeKey = currentKey || remainingChosen[activeIdx] || remainingChosen[0];
  const activeScript = activeKey ? scriptsForRender.find((s) => s.key === activeKey) : null;

  useEffect(() => {
    if ((stage === 'fill' || stage === 'record' || stage === 'question') && !activeScript && data) {
      setStage('pick');
    }
  }, [stage, activeScript, data]);

  const onAccepted = async (_clip) => {
    try {
      const d = await api(`/api/submission/${token}`);
      setData(d);
      setCurrentKey(null);
      setStage('pick');
    } catch (e) {
      console.error('refetch after accept failed', e);
      setCurrentKey(null);
      setStage('pick');
    }
  };

  // After a free-form take is accepted (or uploaded), refresh and return to the
  // send screen — free-form is an optional add-on, never part of the required set.
  const onAcceptedFreeform = async (_clip) => {
    setFreeformAutoUpload(false);
    try {
      const d = await api(`/api/submission/${token}`);
      setData(d);
    } catch (e) {
      console.error('refetch after free-form accept failed', e);
    }
    setStage('pick');
  };

  const allowFreeform = !!(data?.collector?.brand && data.collector.brand.allow_freeform);

  if (stage === 'loading') return <PageShell><Loading /></PageShell>;
  if (stage === 'invalid') return <PageShell><InvalidLink error={error} /></PageShell>;
  if (!data) return null;

  const scripts = scriptsForRender;

  // Recording stage breaks out of the standard page shell — full-viewport
  // dark camera-app chrome with its own top bar. We keep the existing
  // The record stage is the only stage that breaks out of PageShell — the
  // new ScriptRecorder owns its own full-page chrome (vt-rec-page with a
  // warm gradient, light topbar, and 2-column body that collapses to a
  // single column under 900px). Per the design handoff.
  if (stage === 'record') {
    if (!activeScript) return null;   // useEffect above will bounce us to pick
    const clipNumber = acceptedScriptKeys.size + 1;
    if (isInterview) {
      const q = activeScript.question || {};
      return (
        <ScriptRecorder
          interview
          questionId={q.id}
          questionText={q.text}
          questionHint={q.hint}
          brand={data?.collector?.brand}
          script={activeScript}
          invite_token={token}
          filledScript={{}}
          onAccepted={onAccepted}
          onCancel={() => setStage('question')}
          maxDurationSeconds={90}
          clipNumber={clipNumber}
          totalClips={requiredClips}
        />
      );
    }
    return (
      <ScriptRecorder
        brand={data?.collector?.brand}
        script={activeScript}
        invite_token={token}
        filledScript={fills[activeKey] || {}}
        onAccepted={onAccepted}
        onCancel={() => setStage('fill')}
        maxDurationSeconds={Math.max(45, (activeScript.duration_target_seconds || 25) + 30)}
        clipNumber={clipNumber}
        totalClips={requiredClips}
      />
    );
  }

  // Practice warm-up recorder. Same component in practice mode: records, plays
  // back, and on approval just moves on. Nothing is uploaded or kept.
  if (stage === 'practiceRecord') {
    const firstName = (data.submission?.customer_name || '').trim().split(/\s+/)[0] || 'there';
    const ps = practiceScript(firstName, practiceFun);
    return (
      <ScriptRecorder
        practice
        brand={data?.collector?.brand}
        script={ps}
        invite_token={token}
        filledScript={{ '0.0': firstName, '1.0': practiceFun }}
        onAccepted={() => setStage('pick')}
        onCancel={() => setStage('practice')}
        onSkip={() => setStage('pick')}
        maxDurationSeconds={30}
      />
    );
  }

  // Free-form testimonial recorder (no teleprompter). Reuses ScriptRecorder in
  // free-form mode; the take is stored as a 'freeform' clip. autoUpload jumps
  // straight to the file chooser when the customer chose "Upload a video".
  if (stage === 'freeform') {
    return (
      <ScriptRecorder
        freeform
        autoUpload={freeformAutoUpload}
        brand={data?.collector?.brand}
        script={{ key: 'freeform', title: 'In your own words', prompts: [] }}
        invite_token={token}
        filledScript={{}}
        onAccepted={onAcceptedFreeform}
        onCancel={() => { setFreeformAutoUpload(false); setStage('submit'); }}
        maxDurationSeconds={90}
      />
    );
  }

  return (
    <PageShell brand={data?.collector?.brand}>
      {/* Keyed wrapper retriggers entry animation on stage transition. */}
      <div key={stage} className="animate-fade-in-up">
        {stage === 'welcome' && (
          <Welcome
            collector={data.collector}
            customerName={data.submission?.customer_name}
            onStart={() => setStage('practice')}
          />
        )}

        {stage === 'practice' && (
          <PracticeIntro
            customerName={data.submission?.customer_name}
            funValue={practiceFun}
            setFunValue={setPracticeFun}
            onStart={() => setStage('practiceRecord')}
            onSkip={() => setStage('pick')}
          />
        )}

        {stage === 'pick' && (
          <PickScripts
            interview={isInterview}
            scripts={scripts}
            requiredClips={requiredClips}
            acceptedScriptKeys={acceptedScriptKeys}
            collector={data.collector}
            allowFreeform={allowFreeform}
            onAddFreeform={() => { setStage('freeform'); }}
            onPickOne={(key) => {
              setChosenKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
              setCurrentKey(key);
              setStage(isInterview ? 'question' : 'fill');
            }}
            onSubmit={() => setStage('submit')}
          />
        )}

        {stage === 'question' && activeScript && (
          <QuestionIntro
            script={activeScript}
            index={chosenKeys.filter((k) => !acceptedScriptKeys.has(k)).indexOf(activeKey)}
            total={requiredClips}
            doneCount={acceptedScriptKeys.size}
            onBack={() => { setCurrentKey(null); setStage('pick'); }}
            onReady={() => setStage('record')}
          />
        )}

        {stage === 'fill' && activeScript && (
          <FillBlanks
            script={activeScript}
            value={fills[activeKey] || {}}
            onChange={(v) => setFills({ ...fills, [activeKey]: v })}
            onBack={() => { setCurrentKey(null); setStage('pick'); }}
            onContinue={() => setStage('record')}
            inviteToken={token}
          />
        )}

        {stage === 'between' && (
          <Between
            acceptedCount={acceptedScriptKeys.size}
            requiredClips={requiredClips}
            chosenKeys={chosenKeys}
            scripts={scriptsForRender}
            clips={data.clips || []}
            collector={data.collector}
            onNext={() => { setCurrentKey(null); setStage('pick'); }}
            onSubmit={() => setStage('submit')}
          />
        )}

        {stage === 'submit' && (
          <SubmitForm
            collector={data.collector}
            submission={data.submission}
            scripts={scriptsForRender}
            clips={data.clips || []}
            acceptedScriptKeys={acceptedScriptKeys}
            chosenKeys={chosenKeys}
            token={token}
            onBack={() => setStage('between')}
            onDone={() => { localStorage.removeItem(lsKey); navigate(`/r/${token}/thanks`); }}
          />
        )}
      </div>
    </PageShell>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Shell, loading, invalid
// ───────────────────────────────────────────────────────────────────────

function PageShell({ children, brand = null }) {
  // Standard customer-flow shell: warm off-white background + simple header
  // (no support phone) + simple footer (no support phone). The work area is
  // a 760px-max-width column with generous side padding, matching the
  // design's `.vt-page` rules but expressed via Tailwind so layout debugging
  // stays consistent with the rest of the app.
  //
  // The record stage breaks out of this shell entirely — the recorder owns
  // its own full-page chrome (vt-rec-page).
  return (
    <div className="vt-app min-h-screen flex flex-col">
      <RavePilotHeader brand={brand} />
      <main className="flex-1 w-full max-w-[760px] mx-auto px-4 md:px-6 py-6 md:py-10">{children}</main>
      <RavePilotFooter />
    </div>
  );
}

function Loading() {
  return (
    <div className="py-20 flex flex-col items-center justify-center gap-4">
      <div className="flex gap-1.5" aria-label="Loading">
        <span className="w-2.5 h-2.5 rounded-full bg-ravepilot-orange" style={{ animation: 'vt-loader-bounce 1.2s ease-in-out infinite', animationDelay: '0ms' }} />
        <span className="w-2.5 h-2.5 rounded-full bg-ravepilot-orange" style={{ animation: 'vt-loader-bounce 1.2s ease-in-out infinite', animationDelay: '150ms' }} />
        <span className="w-2.5 h-2.5 rounded-full bg-ravepilot-orange" style={{ animation: 'vt-loader-bounce 1.2s ease-in-out infinite', animationDelay: '300ms' }} />
      </div>
      <div className="text-ravepilot-muted text-recorder-base">Loading your invitation…</div>
    </div>
  );
}

function InvalidLink({ error }) {
  // Error state IS the place where the support phone earns its keep —
  // a broken invite token leaves the customer with no path forward.
  // (The "no support phone on the happy path" directive from Felix
  // applies to the recording flow itself, not error recovery.)
  return (
    <div className="vt-paper text-center py-8 md:py-10">
      <h1 className="vt-h-screen">This link isn't valid.</h1>
      <p className="vt-lead">{error || 'Please use the link from your invitation email.'}</p>
      <a className="vt-btn vt-btn-primary vt-tap" href="tel:8444864759">Call (844) 486-4759 for help</a>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Welcome — earnings hero + examples strip + personalized greeting
// ───────────────────────────────────────────────────────────────────────

// Practice warm-up script. A short, friendly fill-in-the-blank read so the
// customer can get comfortable, learn the interface, and confirm their camera +
// mic look and sound good before any real take. Throwaway: never uploaded.
function practiceScript(firstName, funAnswer) {
  return {
    key: '__practice__',
    title: 'Quick practice run',
    duration_target_seconds: 12,
    prompts: [
      { label: 'Say hello', template: 'Hi there! My name is ___, and I think today is going to be a good day.', blanks: [firstName] },
      { label: 'Just for fun', template: 'If I had a free afternoon, I would spend it ___.', blanks: [funAnswer] },
    ],
  };
}

// The fun, tappable answer chips for the practice blank. No keyboard required,
// which keeps it friendly for everyone. They can still type their own.
const PRACTICE_FUN_CHIPS = [
  'out in the garden',
  'with my grandkids',
  'watching an old movie',
  'on the golf course',
  'reading a good book',
];

function PracticeIntro({ customerName, funValue, setFunValue, onStart, onSkip }) {
  const firstName = (customerName || '').trim().split(/\s+/)[0] || 'there';
  return (
    <div className="vt-practice-intro">
      <div className="animate-fade-in-up text-center" style={{ marginBottom: 18 }}>
        <div className="vt-eyebrow justify-center" style={{ marginBottom: 10 }}>
          <span className="vt-bar" />
          Hi {firstName}
        </div>
        <h1 className="vt-h-display vt-welcome-title">Let's do a quick practice run first.</h1>
        <p className="vt-lead vt-welcome-lede">
          Before anything that counts, let's warm up with one fun line. This take is{' '}
          <strong className="vt-muted-strong">just for you</strong>. We won't keep it or share it.
          When you're happy, we'll do a quick automatic check that your camera and lighting look great. The frames are only used for that check, never saved.
        </p>
      </div>

      <div className="vt-paper vt-practice-card animate-fade-in-up" style={{ animationDelay: '80ms' }}>
        <div className="vt-practice-prompt">
          <span className="vt-practice-emoji" aria-hidden="true">😊</span>
          <div>
            <div className="vt-practice-line">
              "Hi there! My name is <strong>{firstName}</strong>, and I think today is going to be a good day.
              If I had a free afternoon, I would spend it <strong>{funValue || '______'}</strong>."
            </div>
          </div>
        </div>

        <div className="vt-practice-fun">
          <div className="vt-practice-fun-label">Pick how you'd finish that last part (just for fun):</div>
          <div className="vt-practice-chips">
            {PRACTICE_FUN_CHIPS.map((chip) => (
              <button
                key={chip}
                type="button"
                className={'vt-practice-chip vt-tap' + (funValue === chip ? ' active' : '')}
                onClick={() => setFunValue(chip)}
              >
                {chip}
              </button>
            ))}
          </div>
          <input
            className="vt-practice-fun-input"
            type="text"
            value={funValue}
            maxLength={60}
            onChange={(e) => setFunValue(e.target.value)}
            placeholder="…or type your own"
            aria-label="Your fun answer"
          />
        </div>
      </div>

      <div className="vt-practice-cta animate-fade-in-up" style={{ animationDelay: '160ms' }}>
        <button className="vt-btn vt-btn-primary vt-btn-lg vt-tap" onClick={onStart}>
          Start practice →
        </button>
        <p className="vt-practice-microtip">5-second countdown, then just read the line. Redo as many times as you like.</p>
        <button type="button" className="vt-link-button vt-tap vt-practice-skip" onClick={onSkip}>
          I'm comfortable, skip practice
        </button>
      </div>
    </div>
  );
}

function Welcome({ collector, customerName, onStart }) {
  const rw = rewardCopy(collector);
  const approval = (collector.approval_payout_cents / 100).toFixed(0);
  const bonus = (collector.usage_bonus_cents / 100).toFixed(0);
  const totalScripts = (collector.scripts || []).length || (collector.required_clips || 3);
  const max = totalScripts * (collector.approval_payout_cents + collector.usage_bonus_cents) / 100;
  const firstName = (customerName || '').trim().split(/\s+/)[0] || 'there';

  // Feature flag for the "See how others did it" examples strip. Set
  // false until we have at least 2-3 real customer testimonials to
  // showcase — the placeholder content (currently the RavePilot marketing
  // video duplicated) doesn't match the "real customers" framing in the
  // section subhead. Flip back to true once we have real submissions
  // approved + the example data below points at them. Data + render
  // code stays in place so this is a one-character revert.
  const SHOW_EXAMPLES = false; // hidden until we have real RavePilot example videos

  // Playback state for the "See how others did it" example cards. null
  // when nothing's playing; a clip-shaped object when the modal renders.
  // We pre-build the object in the example data so onClick is just a
  // direct setPlayingClip(card.clip).
  const [playingClip, setPlayingClip] = useState(null);

  // Example videos for the "See how others did it" strip. Tied to real
  // Cloudflare Stream videos (not portrait silhouettes), so customers see
  // an actual face on the thumb and get an authentic preview when they
  // tap. Both cards currently point at the same sample video as a
  // placeholder -- Felix will swap in real customer testimonials soon.
  //
  // Shape mirrors what /api/submission returns for a real clip so we can
  // reuse ClipPlayerOverlay without a special case. iframe_url +
  // thumbnail_url are computed inline from the Stream UID using the
  // same customer subdomain the worker uses.
  const STREAM_SUBDOMAIN = 'customer-zzob661ztw2o5t2h';
  const exampleClip = (uid, label) => ({
    id: `example-${uid}`,
    script_key: `example-${label}`,
    iframe_url: `https://${STREAM_SUBDOMAIN}.cloudflarestream.com/${uid}/iframe?preload=metadata`,
    thumbnail_url: `https://${STREAM_SUBDOMAIN}.cloudflarestream.com/${uid}/thumbnails/thumbnail.jpg?time=2s&height=400`,
    watch_url: `https://${STREAM_SUBDOMAIN}.cloudflarestream.com/${uid}/watch`,
  });
  const examples = [
    {
      name: 'Dan',
      title: 'Why I switched to RavePilot',
      blurb: 'What finally made the switch worth it, in his own words.',
      duration: '0:55',
      clip: exampleClip('24aeb059bb7b2ea70cd933f9c0dee08a', 'dan'),
    },
    {
      name: 'Taylor G.',
      title: 'My RavePilot story',
      blurb: 'Why he made the call, and what changed after.',
      duration: '0:41',
      clip: exampleClip('278346403cfbf50b0a589ea7ff2bc7de', 'taylor'),
    },
  ];

  return (
    <div className="vt-welcome-page">
      <div className="animate-fade-in-up text-center mb-4">
        <div className="vt-eyebrow justify-center" style={{ marginBottom: 10 }}>
          <span className="vt-bar"></span>Hi, {firstName}
        </div>
        <h1 className="vt-h-display vt-welcome-title">{`We'd love to hear your ${rw.name} story.`}</h1>
        {/* v3 lede: trade the "$X minimum" framing (sells the floor) for a
            full-picture lede (count, time, ownership). Boomers respond
            better to "here's what you're signing up for" than to a single
            dollar number — the earnings hero below carries the money. */}
        <p className="vt-lead vt-welcome-lede">
          {totalScripts} short videos. About 15 minutes. Our thank-you is {rw.welcomeLede}.
        </p>
      </div>

      {/* Reward hero — the free month is the headline. */}
      <div className="vt-earnings-hero animate-fade-in-up" style={{ animationDelay: '80ms' }}>
        <div className="vt-hero-glow"></div>
        <div className="vt-eyebrow justify-center"><span className="vt-bar"></span>Our thank-you</div>
        <div className="vt-amount animate-scale-in">
          <span className="tabular-nums">{rw.heroAmount}</span>
        </div>
        <div className="vt-sub">{rw.heroSub}</div>
      </div>

      <div className="vt-welcome-cta animate-fade-in-up" style={{ animationDelay: '160ms' }}>
        <button className="vt-btn vt-btn-primary vt-btn-lg vt-tap" onClick={onStart}>
          Get started →
        </button>
        <p className="vt-welcome-microtip">No app to install. Re-record as many times as you want.</p>
      </div>

      {/* Examples strip — two example videos at the topics customers
          can record. Replaces the old name+age + portrait silhouette
          treatment because surfacing the *topic* (what kind of story
          to tell) is what actually helps the customer plan, while
          name+age was filler that didn't move the needle.

          Hidden behind SHOW_EXAMPLES (see top of Welcome). We don't
          have real customer testimonials to feature yet -- showing the
          RavePilot marketing video here would undercut the "real customers"
          framing in the section subhead, so we hide the whole strip
          until we have approved submissions to swap in. */}
      {SHOW_EXAMPLES && (
        <div className="vt-examples-strip animate-fade-in-up" style={{ animationDelay: '240ms' }}>
          <div className="vt-examples-head">
            <h3 className="vt-examples-title">See how others did it</h3>
            <p className="vt-examples-sub">Real RavePilot customers. About a minute each.</p>
          </div>
          <div className="vt-examples-row">
            {examples.map((ex, i) => (
              <button
                key={ex.title}
                type="button"
                className="vt-example-card vt-tap"
                onClick={() => setPlayingClip(ex.clip)}
                aria-label={`Play example: ${ex.title}`}
              >
                <span className="vt-example-thumb" data-shade={i === 0 ? 'warm' : 'cool'}>
                  {/* Real Stream poster image; falls back to the silhouette
                      if the URL fails to load (rare, but Stream CDN can
                      blip and we don't want a broken-image icon). */}
                  <img
                    className="vt-example-poster"
                    src={ex.clip.thumbnail_url}
                    alt=""
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className="vt-example-portrait" aria-hidden="true">
                    <span className="vt-ep-head"></span>
                    <span className="vt-ep-shoulders"></span>
                  </span>
                  <span className="vt-example-play">
                    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                      <path d="M6 4l14 8-14 8z" fill="currentColor" />
                    </svg>
                  </span>
                  <span className="vt-example-dur">{ex.duration}</span>
                </span>
                <span className="vt-example-meta">
                  <span className="vt-example-name">{ex.name}</span>
                  <span className="vt-example-quote">"{ex.blurb}"</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {playingClip && (
        <ClipPlayerOverlay
          clip={playingClip}
          script={{ title: examples.find((e) => e.clip.id === playingClip.id)?.title || 'Example' }}
          onClose={() => setPlayingClip(null)}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// PickScripts — icon tiles, lead-copy payout, live earnings tracker
// ───────────────────────────────────────────────────────────────────────

// Per-script icon glyph + tint. Picker is identity-by-key, not by bonus flag —
// the +$20 usage bonus applies to every clip equally, so no card gets a money
// distinction. What varies card-to-card is the *topic*, which is what the
// glyph/tint communicates.
const SCRIPT_ICONS = {
  videos:  { glyph: '▶', tint: 'var(--ravepilot-orange)' },
  reviews: { glyph: '★', tint: 'var(--ravepilot-orange-press)' },
  rewards: { glyph: '$', tint: '#1A7A4D' },
  real:    { glyph: '♥', tint: '#D43A6F' },
};
const DEFAULT_ICON = { glyph: '✦', tint: 'var(--ravepilot-orange)' };


// ── Interview mode: watch the question, then answer ────────────────────────
// Plays the brand's question clip (Stream iframe, autoplay) with the question
// text and optional hints underneath, then hands off to the recorder. Watch
// and record are never on screen together, which keeps phones simple.
function QuestionIntro({ script, index, total, doneCount, onBack, onReady }) {
  const q = script.question || {};
  const n = doneCount + 1;
  return (
    <div>
      <Stepper stage="pick" />
      <div className="animate-fade-in-up" style={{ marginBottom: 18 }}>
        <div className="vt-eyebrow"><span className="vt-bar"></span>Question {n} of {total}</div>
        <h1 className="vt-h-screen">{q.text}</h1>
        <p className="vt-lead">Watch the question first. When you are ready, record your answer in your own words.</p>
      </div>

      {q.iframe_url && (
        <div className="animate-fade-in-up rounded-2xl overflow-hidden bg-black" style={{ position: 'relative', paddingTop: '56.25%', boxShadow: '0 18px 40px -22px rgba(22,27,36,0.35)' }}>
          <iframe
            src={q.iframe_url}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title="Question video"
          />
        </div>
      )}

      {q.hint && (
        <div className="animate-fade-in-up mt-4 rounded-xl border border-black/10 bg-white p-4">
          <div style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.55, marginBottom: 6 }}>Things you could mention</div>
          <div style={{ lineHeight: 1.65 }}>{q.hint}</div>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3">
        <button type="button" className="vt-btn vt-btn-primary vt-btn-lg vt-tap" onClick={onReady}>
          I watched it, record my answer
        </button>
        <button type="button" className="vt-btn vt-btn-secondary vt-tap" onClick={onBack}>
          Pick a different question
        </button>
      </div>
    </div>
  );
}

function PickScripts({ interview = false, scripts, requiredClips, acceptedScriptKeys, collector, onPickOne, onSubmit, allowFreeform, onAddFreeform }) {
  const rw = rewardCopy(collector);
  const doneCount = acceptedScriptKeys.size;
  const remainingScripts = scripts.filter((s) => !acceptedScriptKeys.has(s.key));
  const allDone = remainingScripts.length === 0;
  const canSubmit = doneCount >= 1;
  // Free-form shows as an extra "script" once they've recorded at least one
  // real script, and disappears once they've added a free-form take (like a
  // recorded script leaving the list).
  const showFreeform = !!allowFreeform && doneCount >= 1 && !acceptedScriptKeys.has('freeform');
  const coinSlots = Math.max(3, scripts.length);

  return (
    <div className="vt-pick-page">
      <Stepper stage="pick" />

      <div className="animate-fade-in-up" style={{ marginBottom: 22 }}>
        <div className="vt-eyebrow"><span className="vt-bar"></span>Pick &amp; write</div>
        <h1 className="vt-h-screen">{interview
          ? (doneCount === 0 ? 'Pick a question to answer.' : allDone ? "That's every question answered." : 'Answer another question?')
          : (doneCount === 0 ? 'Pick a story to record.' : allDone ? "That's every story recorded." : 'Record another story?')}</h1>
        <p className="vt-lead">
          {doneCount === 0
            ? <>Tap one to record it now. You can come back for more after each one. Share one we accept and your <strong style={{ color: '#161B24' }}>{rw.pickStrong}</strong> is on its way{rw.pickAfter}</>
            : allDone
            ? <>Nice work. You have recorded every story. Submit when you are ready and your <strong style={{ color: '#161B24' }}>{rw.pickStrong}</strong> is on its way{rw.pickAfter}</>
            : <>Nice work. Tap another story to record it, or submit what you have. Every accepted video earns your <strong style={{ color: '#161B24' }}>{rw.pickStrong}</strong>{rw.pickAfter}</>}
        </p>
      </div>

      {(!allDone || showFreeform) && <div className="vt-script-list">
        {!allDone && remainingScripts.map((s, i) => {
          const ico = SCRIPT_ICONS[s.key] || DEFAULT_ICON;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onPickOne(s.key)}
              className="vt-script-card vt-tap animate-fade-in-up"
              style={{ animationDelay: `${0.06 + i * 0.06}s` }}
            >
              {interview && s.question && s.question.thumbnail_url ? (
                <span className="vt-script-icon" style={{ padding: 0, overflow: 'hidden' }}>
                  <img src={s.question.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                </span>
              ) : (
                <span className="vt-script-icon" style={{ ['--vt-ico']: ico.tint }}>
                  <span className="vt-script-glyph">{ico.glyph}</span>
                </span>
              )}

              <div className="vt-script-body">
                <div className="vt-script-title">{s.title}</div>
                <div className="vt-script-summary">{s.summary}</div>
              </div>

              <span className="vt-script-check" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" opacity=".3" />
                  <polygon points="10 8 16 12 10 16" fill="currentColor" stroke="none" />
                </svg>
              </span>
            </button>
          );
        })}
        {showFreeform && (
          <button
            type="button"
            onClick={() => onAddFreeform && onAddFreeform()}
            className="vt-script-card vt-tap animate-fade-in-up"
            style={{ animationDelay: `${0.06 + remainingScripts.length * 0.06}s` }}
          >
            <span className="vt-script-icon" style={{ ['--vt-ico']: '#6461FC' }}>
              <span className="vt-script-glyph">✎</span>
            </span>

            <div className="vt-script-body">
              <div className="vt-script-title">In your own words</div>
              <div className="vt-script-summary">No script. Say whatever you'd like, or upload a video you already have.</div>
            </div>

            <span className="vt-script-check" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" opacity=".3" />
                <polygon points="10 8 16 12 10 16" fill="currentColor" stroke="none" />
              </svg>
            </span>
          </button>
        )}
      </div>}

      <div
        className={'vt-earn-tracker animate-fade-in-up' + (doneCount > 0 ? ' on' : '')}
        aria-live="polite"
      >
        <div className="vt-earn-stack">
          {Array.from({ length: coinSlots }).map((_, ci) => (
            <span key={ci} className={'vt-earn-coin' + (ci < doneCount ? ' filled' : '')}>✦</span>
          ))}
        </div>
        <div className="vt-earn-text">
          <span className="vt-earn-label">{doneCount === 0 ? 'Tap a story to start' : `${doneCount} recorded, nice`}</span>
          <span className="vt-earn-amounts">
            <strong className="vt-tabular">{rw.earnStrong}</strong>
            <span className="vt-earn-up">{rw.earnUp}</span>
          </span>
        </div>
      </div>

      {canSubmit && (
        <div className="vt-actions">
          <span className="vt-grow"></span>
          <button className="vt-btn vt-btn-primary vt-btn-lg vt-tap" onClick={onSubmit}>
            Submit {doneCount === 1 ? 'my video' : `my ${doneCount} videos`} →
          </button>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// FillBlanks — numbered prompt cards, glued punctuation, teleprompter preview
// ───────────────────────────────────────────────────────────────────────

function FillBlanks({ script, value, onChange, onBack, onContinue, inviteToken }) {
  const [listening, setListening] = useState(null);             // {pi, bi} | null
  const [supportsVoice, setSupportsVoice] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [polishResult, setPolishResult] = useState(null);       // { original, polished, changed }
  const [polishError, setPolishError] = useState(null);
  const recognitionRef = useRef(null);

  // Feature-detect SpeechRecognition once on mount. Safari/Chrome on
  // most modern devices have webkitSpeechRecognition; we hide the mic
  // affordance on browsers that don't (so we never show a button that
  // does nothing when tapped).
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupportsVoice(!!SR);
  }, []);

  const setBlank = (promptIdx, blankIdx, text) => {
    const key = `${promptIdx}.${blankIdx}`;
    onChange({ ...value, [key]: text });
  };

  // ── Voice input ──────────────────────────────────────────────────
  // Web Speech API. interimResults=true so the text fills in as they
  // speak (vs only landing at the end). continuous=false so we stop at
  // a natural pause rather than recording until they tap stop.
  const startVoice = (pi, bi) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
    }
    const r = new SR();
    r.lang = 'en-US';
    r.interimResults = true;
    r.continuous = false;
    r.maxAlternatives = 1;
    setListening({ pi, bi });
    let finalText = '';
    r.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += t;
        else interim += t;
      }
      setBlank(pi, bi, (finalText + interim).trim());
    };
    r.onerror = () => setListening(null);
    r.onend = () => setListening(null);
    recognitionRef.current = r;
    try { r.start(); } catch { setListening(null); }
  };

  const stopVoice = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
    }
    setListening(null);
  };

  // Cleanup any in-flight recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* ignore */ }
      }
    };
  }, []);

  // ── Progress derivation ───────────────────────────────────────────
  const totalBlanks = script.prompts.reduce((n, p) => n + p.blanks.length, 0);
  const filledBlanks = script.prompts.reduce(
    (n, p, pi) => n + p.blanks.filter((_, bi) => (value[`${pi}.${bi}`] || '').trim().length > 0).length,
    0
  );
  const allFilled = filledBlanks === totalBlanks;
  const progressPct = totalBlanks > 0 ? Math.round((filledBlanks / totalBlanks) * 100) : 0;

  // ── AI polish ────────────────────────────────────────────────────
  // Calls POST /api/polish-fills which hits Claude Haiku and returns
  // { original, polished, changed }. The endpoint validates against the
  // collector's scripts to make sure no extra keys sneak through.
  const polishScript = async () => {
    setPolishing(true);
    setPolishError(null);
    setPolishResult(null);
    try {
      // Build the drafts map. Only include keys that are actually filled
      // — empties would just bloat the prompt and waste tokens.
      const drafts = {};
      script.prompts.forEach((p, pi) => {
        p.blanks.forEach((_, bi) => {
          const k = `${pi}.${bi}`;
          const v = (value[k] || '').trim();
          if (v.length > 0) drafts[k] = v;
        });
      });
      const result = await api('/api/polish-fills', {
        method: 'POST',
        body: { invite_token: inviteToken, script_key: script.key, drafts },
      });
      setPolishResult(result);
    } catch (e) {
      setPolishError(e?.message || "We couldn't polish right now. Try again in a minute.");
    } finally {
      setPolishing(false);
    }
  };

  const acceptPolish = () => {
    if (!polishResult) return;
    onChange({ ...value, ...polishResult.polished });
    setPolishResult(null);
  };
  const declinePolish = () => setPolishResult(null);

  return (
    <>
      <Stepper stage="fill" />
      <div className="vt-fill-page animate-fade-in-up" style={{ marginBottom: 18 }}>
        <div className="vt-eyebrow"><span className="vt-bar"></span>{script.title}</div>
        <h1 className="vt-h-screen">Tell us your story before you record.</h1>
        <p className="vt-lead">
          Fill in your real answers below.
          {supportsVoice && <> Tap the microphone to speak instead of typing.</>}
        </p>
      </div>

      <div>
        {script.prompts.map((p, pi) => {
          const promptFilled = p.blanks.every((_, bi) => (value[`${pi}.${bi}`] || '').trim().length > 0);
          return (
            <div
              key={pi}
              className={'vt-prompt-card animate-fade-in-up' + (promptFilled ? ' filled' : '')}
              style={{ animationDelay: `${0.08 + pi * 0.06}s` }}
            >
              <div className="vt-prompt-head">
                <span className="vt-prompt-num">
                  {promptFilled
                    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    : pi + 1}
                </span>
                <span className="vt-prompt-label">{p.label}</span>
              </div>
              <div className="vt-prompt-body">
                {renderPromptWithInputs(p, value, pi, setBlank, {
                  supportsVoice,
                  listening,
                  startVoice,
                  stopVoice,
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Polish CTA — appears only when everything is filled in. We
          gate it on allFilled so Claude isn't trying to make sense of
          half-written answers. */}
      {allFilled && !polishResult && (
        <div className="vt-polish-strip animate-fade-in-up">
          <button
            type="button"
            className={'vt-polish-cta vt-tap' + (polishing ? ' busy' : '')}
            onClick={polishScript}
            disabled={polishing}
          >
            {polishing ? (
              <>
                <span className="vt-submit-spinner" aria-hidden="true" />
                <span className="vt-polish-text">
                  <strong>Reading your story…</strong>
                  <span>Hang tight, this takes a few seconds.</span>
                </span>
              </>
            ) : (
              <>
                <span className="vt-polish-spark" aria-hidden="true">✨</span>
                <span className="vt-polish-text">
                  <strong>Make this sound natural</strong>
                  <span>We'll gently clean it up. You decide.</span>
                </span>
              </>
            )}
          </button>
          {polishError && <div className="vt-polish-error">{polishError}</div>}
        </div>
      )}

      {/* Polish ran but Claude didn't find anything to change. Friendly
          "you're already good" pat on the back. */}
      {polishResult && !polishResult.changed && (
        <div className="vt-polish-noop animate-fade-in-up">
          <span className="vt-polish-noop-ico">✓</span>
          <div>
            <strong>Sounds great as-is.</strong>
            <span>We read it through and didn't find anything worth changing.</span>
          </div>
          <button
            type="button"
            className="vt-polish-noop-close"
            onClick={declinePolish}
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}

      {polishResult && polishResult.changed && (
        <PolishCompare
          script={script}
          original={polishResult.original}
          polished={polishResult.polished}
          onAccept={acceptPolish}
          onDecline={declinePolish}
        />
      )}

      <details className="vt-disclosure animate-fade-in-up" style={{ marginTop: 8 }}>
        <summary>
          <span className="vt-chev">›</span>
          See what you'll read on camera
        </summary>
        <div className="vt-tele-preview">
          {renderTeleprompterJSX(script, value)}
        </div>
      </details>

      {/* Sticky bottom action bar — progress bar instead of "N of M
          filled" text. More legible at a glance, and fills nicely
          beside the back/continue buttons on mobile. */}
      <div className="vt-fill-bottom">
        <div className="vt-fill-progress" aria-live="polite">
          <div className="vt-fill-progress-track">
            <span className="vt-fill-progress-bar" style={{ width: `${progressPct}%` }} />
          </div>
          <span className={'vt-fill-progress-label' + (allFilled ? ' done' : '')}>
            {allFilled
              ? <>All set <span aria-hidden="true">✓</span></>
              : <>{filledBlanks}/{totalBlanks} filled</>}
          </span>
        </div>
        <div className="vt-fill-actions">
          <button className="vt-btn vt-btn-secondary vt-tap vt-fill-back" onClick={onBack} aria-label="Back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            <span className="vt-fill-back-text">Back</span>
          </button>
          <button
            className="vt-btn vt-btn-primary vt-btn-lg vt-tap vt-fill-continue"
            disabled={!allFilled}
            onClick={onContinue}
          >
            {allFilled ? 'On to recording →' : 'Fill every blank'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── PolishCompare ─────────────────────────────────────────────────────
// Side-by-side comparison panel. Renders both versions as plain text
// (script template with values inlined) so the customer can read each
// out loud and feel the difference. Two big buttons: 'Keep mine' vs
// 'Use the polished version →'.
function PolishCompare({ script, original, polished, onAccept, onDecline }) {
  const render = (fills) => script.prompts.map((p, pi) => {
    const parts = p.template.split('___');
    return parts.map((seg, i) => {
      if (i < parts.length - 1) {
        const v = (fills[`${pi}.${i}`] || '').trim();
        return seg + (v || '_____');
      }
      return seg;
    }).join('');
  }).join('\n\n');

  return (
    <div className="vt-polish-compare animate-fade-in-up" role="region" aria-label="Polished version preview">
      <div className="vt-polish-compare-head">
        <span className="vt-eyebrow" style={{ margin: 0 }}>
          <span className="vt-bar"></span>Polished
        </span>
        <h3 className="vt-polish-compare-title">Here's a smoother version</h3>
        <p className="vt-polish-compare-sub">
          We kept your story. Just tidied the words a little. Pick whichever feels more like you.
        </p>
      </div>

      <div className="vt-polish-compare-grid">
        <div className="vt-polish-version">
          <span className="vt-polish-version-tag">Your words</span>
          <div className="vt-polish-version-body">{render(original)}</div>
        </div>
        <div className="vt-polish-version vt-polish-version-new">
          <span className="vt-polish-version-tag">Polished</span>
          <div className="vt-polish-version-body">{render(polished)}</div>
        </div>
      </div>

      <div className="vt-polish-compare-actions">
        <button className="vt-btn vt-btn-secondary vt-tap" onClick={onDecline}>Keep mine</button>
        <button className="vt-btn vt-btn-primary vt-tap" onClick={onAccept}>
          Use the polished version →
        </button>
      </div>
    </div>
  );
}

// Render the prompt template with inline <input>s for each blank. Trailing
// punctuation gets glued to the input via `white-space: nowrap` so a period
// can't get stranded on its own line when the input wraps.
//
// `voice` param (optional): when set, the inputs get a tiny mic button
// alongside them that toggles Web Speech recognition for that blank.
// Shape: { supportsVoice, listening: {pi,bi}|null, startVoice(pi,bi), stopVoice() }
function renderPromptWithInputs(prompt, fills, promptIdx, setBlank, voice) {
  const parts = prompt.template.split('___');
  const blanks = prompt.blanks || [];
  const out = [];
  if (parts[0]) out.push(<span key="t0">{parts[0]}</span>);

  for (let i = 0; i < parts.length - 1; i++) {
    const next = parts[i + 1] || '';
    // Peel leading punctuation off the next text segment so it stays glued
    // to the input. If the punctuation is followed by whitespace or EOS,
    // that's a sentence ending that should not float free.
    const m = next.match(/^([.,?!;:]+(?:\s|$))([\s\S]*)$/);
    const punct = m ? m[1].replace(/\s+$/, '') : '';
    const rest  = m ? m[2] : next;

    // If the template text immediately before this input ends with '$',
    // it's a currency field. Two behaviors flow from that:
    //   1. inputMode='decimal' — opens the number keypad on mobile so
    //      the customer isn't hunting for the dollar key
    //   2. Strip any leading '$' the customer reflexively types, so a
    //      template like "...was $___ a month" doesn't end up reading
    //      "...was $$78 a month" in the teleprompter
    const isCurrency = parts[i].endsWith('$');

    const v = fills[`${promptIdx}.${i}`] || '';
    const filled = v.trim().length > 0;

    // Placeholder hint comes from prompt.blanks[i] in the script data,
    // not a hardcoded string. Lets each blank show a concrete example
    // ("AT&T", "78", "my daughter Karen") instead of a generic "your
    // answer". Falls back to "your answer" if the data has nothing.
    const placeholder = blanks[i] || 'your answer';

    // Input width: when filled, size to the value (capped 12–36ch).
    // When empty, size to the placeholder length so a short hint like
    // "78" doesn't sit in a 14ch box and a long one like "my daughter
    // Karen" doesn't get truncated. Floor at 8 so even a 1-character
    // hint has enough room to look like a fillable field.
    const ch = filled
      ? Math.max(12, Math.min(36, v.length + 3))
      : Math.max(8,  Math.min(36, placeholder.length + 3));

    const isListening = voice && voice.listening
      && voice.listening.pi === promptIdx
      && voice.listening.bi === i;

    out.push(
      <span key={`g${i}`} className="vt-blank-group" style={{ whiteSpace: 'nowrap' }}>
        <span className={'vt-blank-shell' + (filled ? ' filled' : '') + (isListening ? ' listening' : '')}>
          <input
            type="text"
            inputMode={isCurrency ? 'decimal' : 'text'}
            value={v}
            onChange={(e) => {
              let val = e.target.value;
              if (isCurrency) val = val.replace(/^\$+/, '');
              setBlank(promptIdx, i, val);
            }}
            placeholder={isListening ? 'Listening…' : placeholder}
            className={'vt-blank-input' + (filled ? ' filled' : '')}
            style={{ width: `${ch}ch` }}
          />
          {voice && voice.supportsVoice && (
            <button
              type="button"
              className={'vt-blank-mic' + (isListening ? ' on' : '')}
              onClick={() => {
                if (isListening) voice.stopVoice();
                else voice.startVoice(promptIdx, i);
              }}
              aria-label={isListening ? 'Stop listening' : 'Speak your answer'}
              tabIndex={-1}
            >
              {isListening ? (
                <span className="vt-mic-pulse" aria-hidden="true">
                  <span></span><span></span><span></span>
                </span>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="9" y="3" width="6" height="12" rx="3"/>
                  <path d="M5 11a7 7 0 0 0 14 0"/>
                  <line x1="12" y1="18" x2="12" y2="22"/>
                </svg>
              )}
            </button>
          )}
        </span>
        {punct && <span className="vt-blank-tail">{punct}</span>}
      </span>
    );
    if (rest) out.push(<span key={`t${i + 1}`}>{(punct ? ' ' : '') + rest}</span>);
  }
  return out;
}

// Teleprompter preview — JSX version with filled blanks rendered in
// brand orange so the customer can see what they wrote vs. what's still
// blank at a glance.
function renderTeleprompterJSX(script, fills) {
  const segs = [];
  script.prompts.forEach((p, pi) => {
    if (pi > 0) {
      segs.push(<br key={`br${pi}a`} />);
      segs.push(<br key={`br${pi}b`} />);
    }
    const parts = p.template.split('___');
    parts.forEach((t, i) => {
      if (t) segs.push(<span key={`${pi}t${i}`}>{t}</span>);
      if (i < parts.length - 1) {
        const v = (fills[`${pi}.${i}`] || '').trim();
        if (v) {
          segs.push(<strong key={`${pi}b${i}`} style={{ color: 'var(--ravepilot-orange)', fontWeight: 500 }}>{v}</strong>);
        } else {
          segs.push(<span key={`${pi}b${i}`} className="vt-blank-missing">______</span>);
        }
      }
    });
  });
  return <>{segs}</>;
}

// ───────────────────────────────────────────────────────────────────────
// Between — celebration badge + progress pills + "earned so far"
// ───────────────────────────────────────────────────────────────────────

// ───────────────────────────────────────────────────────────────────────
// Between — handoff v3 redesign
// ───────────────────────────────────────────────────────────────────────
//
// What replaces the old screen:
//   1. Just-recorded hero — stylized clip thumbnail (face/shoulders
//      silhouette) + the recorded story's title + duration. Visually
//      reassuring: "yes, your video really got saved." Decorative for
//      now — no playback wired up (depends on Stream HLS URLs being
//      threaded through). Future iteration: tap to play in a compare
//      overlay against a fresh take.
//   2. First-clip celebration banner — only shows on clip 1. "🎉 Your
//      first one! That's how it goes. The rest gets easier from here."
//      Vanishes from clip 2 onward (boomers don't want repetitive
//      cheerleading).
//   3. Earnings card — orange-tinted panel with the running total and
//      a row of "coins" that fill (with a flip animation) as clips
//      land. One coin per chosenKey, plus a "+bonus if used" subline.
//   4. Story queue — interactive tile list. Each script in chosenKeys
//      renders as a tile with three states: done (green, replay
//      affordance), next (pulsing orange border, → arrow), waiting
//      (greyed dot, disabled). Click "next" to jump to fill. Replaces
//      the old pill strip.
//   5. CTA section — single primary "Start the next story →" button
//      with a context-aware headline above it ("Up next: <title>" or
//      "All done!"). Secondary "I'm done. Submit what I have." link
//      appears once minimum hit but more stories remain.
//
// What I dropped from the handoff:
//   • CompareOverlay (side-by-side video player for retakes) — the
//     handoff scaffolds it but it needs Stream playback URLs threaded
//     into Between. Punt to a future commit; current customers can
//     still re-record by going back to pick.
//   • The 'comparing' state + setComparing onClick handler — same
//     reason as above. The clip thumb is decorative for now (no
//     onClick, no focus ring).
//
// Props now include `scripts` (full collector.scripts list, for tile
// titles) and `clips` (the live clip rows, for the just-recorded
// duration). Parent passes both.
function Between({ acceptedCount, requiredClips, chosenKeys, scripts, clips, collector, onNext, onSubmit }) {
  const rw = rewardCopy(collector);
  const approval = collector.approval_payout_cents / 100;
  const bonus    = collector.usage_bonus_cents / 100;
  const earned   = acceptedCount * approval;

  // Playback overlay state. Tracks the clip the customer asked to replay
  // (either by tapping the just-recorded hero thumbnail or a done tile in
  // the story queue). null = no overlay; an object = render the modal.
  const [playingClip, setPlayingClip] = useState(null);

  // Find the most recent uploaded clip for a given script_key. Used by
  // the hero thumb + each done tile to resolve which actual video to play.
  // Returns the clip row (with iframe_url, thumbnail_url, watch_url
  // baked in by the worker once stream_ready=true) or null.
  const clipForScript = (scriptKey) => {
    if (!scriptKey) return null;
    return (clips || [])
      .filter((c) => c.script_key === scriptKey)
      .sort((a, b) => (b.created_at || 0) - (a.created_at || 0))[0] || null;
  };

  // Build the queue used to drive Between's UI. Normally this is just
  // chosenKeys -- the order the customer picked scripts in. But two
  // real-world conditions break that:
  //   1. Server-side resets (e.g. testing): the submission goes back to
  //      in_progress + clips are preserved, but the client's localStorage
  //      chosenKeys is stale or empty.
  //   2. A customer clearing browser data mid-flow then re-visiting their
  //      magic link.
  // In either case the symptom is identical: chosenKeys is shorter than
  // (or missing) the actual accepted clips, so queue[acceptedCount-1]
  // returns undefined, the hero thumbnail's iframe_url is null, and the
  // spinner sits forever.
  //
  // Fix: treat chosenKeys as a HINT for ordering. If it's empty (or
  // shorter than accepted), backfill from accepted clips ordered by
  // created_at. The customer's actual videos are the source of truth;
  // localStorage is just an optimization.
  const acceptedClipKeysInOrder = (clips || [])
    .filter((c) => c.status === 'uploaded' || c.status === 'approved')
    .sort((a, b) => (a.created_at || 0) - (b.created_at || 0))
    .map((c) => c.script_key);

  // De-dupe in case the customer re-recorded the same script.
  const seen = new Set();
  const acceptedKeysUnique = acceptedClipKeysInOrder.filter((k) =>
    seen.has(k) ? false : (seen.add(k), true)
  );

  // Compose: chosenKeys first (preserves intent), then any accepted-but-
  // not-in-chosenKeys appended. This way the recorder-driven hero+tile
  // logic always has something to point at, while a chosen-but-not-yet-
  // done script still appears in its right place.
  const chosenSet = new Set(chosenKeys || []);
  const queue = [
    ...(chosenKeys || []),
    ...acceptedKeysUnique.filter((k) => !chosenSet.has(k)),
  ];
  const total = queue.length || requiredClips;
  const left  = Math.max(0, total - acceptedCount);
  const done  = left === 0;

  // The most recently recorded script (= the one this Between screen is
  // celebrating). Pulled from the queue index just below acceptedCount.
  // If the queue is somehow still empty (no chosenKeys AND no clips),
  // justKey is undefined and the hero gracefully shows the placeholder
  // silhouette with a generic "Story 1" title -- not a broken UI.
  const justKey = queue[acceptedCount - 1] || acceptedKeysUnique[acceptedKeysUnique.length - 1];
  const justRecorded = scripts.find((s) => s.key === justKey) || null;
  const justClip = clipForScript(justKey);
  const nextKey  = queue[acceptedCount];
  const nextScript = scripts.find((s) => s.key === nextKey) || null;

  // Duration shown on the clip thumbnail — pull the latest uploaded clip
  // for that script_key and format mm:ss. Falls back to a tasteful '0:30'
  // placeholder if duration_seconds isn't recorded (older clips).
  const dur = justClip?.duration_seconds;
  const durLabel = dur && dur > 0
    ? `${Math.floor(dur / 60)}:${String(Math.round(dur % 60)).padStart(2, '0')}`
    : '0:30';

  // Map queue items to renderable tile data. If a queue key isn't in
  // scripts (data drift), we drop it rather than render a blank tile.
  const tiles = queue
    .map((k, i) => ({ script: scripts.find((s) => s.key === k), i }))
    .filter((t) => t.script);

  return (
    <>
      <Stepper stage="between" />

      <div className="vt-between-page">
        {/* Just-recorded hero */}
        <div className="vt-between-hero animate-fade-in-up">
          {acceptedCount === 1 && (
            <div className="vt-first-clip-banner" aria-live="polite">
              <span className="vt-first-clip-emoji" aria-hidden="true">🎉</span>
              <div className="vt-first-clip-text">
                <strong>Your first one! That's how it goes.</strong>
                <span>The rest gets easier from here. Promise.</span>
              </div>
            </div>
          )}
          <div className="vt-between-clip">
            <button
              type="button"
              className={'vt-clip-thumb' + (justClip?.iframe_url ? ' vt-clip-thumb-playable' : '')}
              onClick={() => justClip?.iframe_url && setPlayingClip(justClip)}
              disabled={!justClip?.iframe_url}
              aria-label={
                justClip?.iframe_url
                  ? `Play back your ${justRecorded?.title || 'video'}`
                  : `${rw.name} is polishing your video`
              }
            >
              {/* Real poster from Stream once encoding is done. Until then
                  we keep the decorative face/shoulders silhouette so the
                  layout doesn't pop. */}
              {justClip?.thumbnail_url ? (
                <img
                  className="vt-clip-thumb-poster"
                  src={justClip.thumbnail_url}
                  alt=""
                  loading="lazy"
                  /* If Stream's CDN has a transient 404 right after
                     stream_ready flips true, hide the broken-image icon
                     and let the face/shoulders silhouette show through.
                     The next poll tick will swap the src and try again. */
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <>
                  <div className="vt-clip-thumb-face"></div>
                  <div className="vt-clip-thumb-shoulders"></div>
                </>
              )}
              <div className="vt-clip-thumb-overlay">
                <span className="vt-clip-thumb-play">
                  {justClip?.iframe_url ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M7 4l13 8-13 8z"/>
                    </svg>
                  ) : (
                    /* Spinner while encoding so the customer knows the
                       thumb will become playable soon — not just broken. */
                    <svg width="20" height="20" viewBox="0 0 50 50" aria-hidden="true">
                      <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" strokeWidth="4" strokeOpacity=".25"/>
                      <path fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round"
                            d="M25 5 a20 20 0 0 1 20 20" >
                        <animateTransform attributeName="transform" type="rotate"
                                          from="0 25 25" to="360 25 25" dur="0.9s" repeatCount="indefinite"/>
                      </path>
                    </svg>
                  )}
                </span>
              </div>
              <span className="vt-clip-thumb-time">{durLabel}</span>
            </button>
            <div className="vt-between-clip-meta">
              <span className="vt-eyebrow" style={{ margin: 0 }}>
                <span className="vt-bar"></span>Just recorded
              </span>
              <h2 className="vt-between-clip-title">
                {justRecorded?.title || `Story ${acceptedCount}`}
              </h2>
              <p className="vt-between-clip-sub">
                Saved and ready for our team. You can keep going or stop here — your
                videos are safe.
              </p>
            </div>
          </div>
        </div>

        {/* Reward card */}
        <div className="vt-earnings-card animate-fade-in-up" style={{ animationDelay: '80ms' }}>
          <div className="vt-earnings-card-left">
            <span className="vt-earnings-card-label">Your reward</span>
            <span className="vt-earnings-card-amount">
              <span className="tabular-nums">{rw.cardAmount}</span>
            </span>
            {acceptedCount > 0 && (
              <span className="vt-earnings-card-sub">
                {acceptedCount} {acceptedCount === 1 ? 'story' : 'stories'} recorded · {rw.cardSub}
              </span>
            )}
          </div>
          <div className="vt-earnings-card-coins" aria-hidden="true">
            {Array.from({ length: Math.max(3, total) }).map((_, i) => (
              <span
                key={i}
                className={'vt-big-coin' + (i < acceptedCount ? ' filled' : '')}
                style={{ ['--vt-idx']: i }}
              >
                <span>✦</span>
              </span>
            ))}
          </div>
        </div>

        {/* Story queue */}
        {tiles.length > 0 && (
          <div className="vt-story-queue animate-fade-in-up" style={{ animationDelay: '160ms' }}>
            <div className="vt-story-queue-head">
              <span className="vt-eyebrow" style={{ margin: 0 }}>
                <span className="vt-bar"></span>Your videos
              </span>
              <span className="vt-story-queue-count">
                {acceptedCount} of {tiles.length} done
              </span>
            </div>
            <div className="vt-story-queue-list">
              {tiles.map((t, idx) => {
                const isDone = idx < acceptedCount;
                const isNext = idx === acceptedCount && !done;
                const tileClip = isDone ? clipForScript(t.script.key) : null;
                const canPlay = isDone && tileClip?.iframe_url;
                const cls = 'vt-story-tile'
                  + (isDone ? ' done' : '')
                  + (isNext ? ' next' : '')
                  + (canPlay ? ' playable' : '');
                return (
                  <button
                    key={t.script.key + idx}
                    type="button"
                    className={cls}
                    onClick={
                      isNext ? onNext
                        : canPlay ? () => setPlayingClip(tileClip)
                        : undefined
                    }
                    disabled={!isNext && !canPlay}
                    aria-label={
                      isNext ? `Continue to record: ${t.script.title}`
                        : canPlay ? `Play back your ${t.script.title} video`
                        : isDone ? `${t.script.title} — polishing`
                        : `${t.script.title} — not yet recorded`
                    }
                  >
                    <span className="vt-story-tile-icon">
                      {isDone ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      ) : isNext ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7 4l13 8-13 8z"/>
                        </svg>
                      ) : (
                        <span className="vt-story-tile-dot"></span>
                      )}
                    </span>
                    <span className="vt-story-tile-meta">
                      <span className="vt-story-tile-title">{t.script.title}</span>
                      <span className="vt-story-tile-status">
                        {isDone
                          ? (canPlay ? 'Tap to replay' : 'Polishing…')
                          : isNext ? 'Up next' : 'Waiting'}
                      </span>
                    </span>
                    {isDone && (
                      <span className="vt-story-tile-replay" aria-hidden="true">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7 4l13 8-13 8z"/>
                        </svg>
                      </span>
                    )}
                    {isNext && <span className="vt-story-tile-arrow" aria-hidden="true">→</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Primary CTA */}
        <div className="vt-between-cta animate-fade-in-up" style={{ animationDelay: '240ms' }}>
          <h1 className="vt-between-headline">
            {done
              ? "All done! Ready to send them off?"
              : nextScript
              ? <>Up next: <span className="vt-next-title">{nextScript.title}</span></>
              : <>Pick another story to record.</>}
          </h1>

          {!done && nextScript && (
            <button
              className="vt-btn vt-btn-primary vt-btn-lg vt-tap vt-between-primary"
              onClick={onNext}
            >
              Start the next story →
            </button>
          )}
          {done && (
            <button
              className="vt-btn vt-btn-primary vt-btn-lg vt-tap vt-between-primary"
              onClick={onSubmit}
            >
              Send my videos →
            </button>
          )}

          {/* Secondary submit-now link — only when minimum hit but more
              chosen scripts remain. Keeps the "all done?" path clear when
              everything's recorded, and stays out of the way pre-minimum. */}
          {acceptedCount >= 1 && !done && (
            <button
              className="vt-link-button vt-tap"
              onClick={onSubmit}
              style={{ marginTop: 14 }}
            >
              I'm done. Submit what I have.
            </button>
          )}
        </div>
      </div>

      {playingClip && (
        <ClipPlayerOverlay
          clip={playingClip}
          script={scripts.find((s) => s.key === playingClip.script_key)}
          onClose={() => setPlayingClip(null)}
        />
      )}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────
// SubmitForm — confirmation-card pattern (no form by default), quiet consent
// ───────────────────────────────────────────────────────────────────────

function SubmitForm({ collector, submission, scripts, clips, acceptedScriptKeys, chosenKeys, token, onBack, onDone }) {
  const rw = rewardCopy(collector);
  const initialName  = (submission?.customer_name  || '').trim();
  const initialEmail = (submission?.customer_email || '').trim();
  const [name, setName]   = useState(initialName);
  const [email, setEmail] = useState(initialEmail);
  // The reward email comes from the invitation and is locked here so it
  // can't be changed on the send screen. Only the name is editable, and we
  // open it for entry when no name was prefilled.
  const [editingName, setEditingName] = useState(!initialName);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // Playback overlay state — same pattern as Between and ThankYou. null
  // when nothing's playing; a clip object when the modal should render.
  const [playingClip, setPlayingClip] = useState(null);

  // Resolve a script_key to its most recently uploaded clip. The clip
  // row carries iframe_url / thumbnail_url once Stream encoding is done
  // (populated server-side in /api/submission/:token).
  const clipForScript = (scriptKey) => {
    if (!scriptKey) return null;
    return (clips || [])
      .filter((c) => c.script_key === scriptKey)
      .sort((a, b) => (b.created_at || 0) - (a.created_at || 0))[0] || null;
  };

  const approval = collector.approval_payout_cents / 100;
  const bonus    = collector.usage_bonus_cents / 100;
  const approvalUsd = approval.toFixed(0);
  const bonusUsd    = bonus.toFixed(0);
  const firstName = (name || '').split(/\s+/)[0] || 'there';

  // Build the clip strip — one tile per accepted clip. We resolve the
  // displayed script title from the live scripts list. Falls back to
  // chosenKeys if acceptedScriptKeys is somehow empty (shouldn't happen
  // — we only reach this screen with acceptedCount >= 1 — but keeps the
  // UI from rendering an empty strip on edge cases).
  const recordedKeys = (acceptedScriptKeys && acceptedScriptKeys.size > 0)
    ? Array.from(acceptedScriptKeys)
    : (chosenKeys || []);
  const recordedScripts = recordedKeys
    .map((k) => (k === 'freeform'
        ? { key: 'freeform', title: 'In your own words' }
        : (scripts || []).find((s) => s.key === k)))
    .filter(Boolean);
  const clipCount        = Math.max(1, recordedScripts.length);
  const guaranteedTotal  = clipCount * approval;
  const maxWithBonus     = clipCount * (approval + bonus);

  // Cap-friendly duration label: for each script, pull the matching
  // clip's duration_seconds if present. Otherwise fall back to a polite
  // '~25s' guess (most testimonial clips run 20-30s).
  const durationFor = (scriptKey) => {
    const clip = (clips || [])
      .filter((c) => c.script_key === scriptKey)
      .sort((a, b) => (b.created_at || 0) - (a.created_at || 0))[0];
    const d = clip?.duration_seconds;
    return d && d > 0 ? `~${Math.round(d)}s` : '~25s';
  };

  const submit = async () => {
    setErr(null); setSubmitting(true);
    try {
      await api(`/api/submission/${token}/submit`, {
        method: 'POST',
        body: {
          customer_name: name.trim(),
          customer_email: email.trim(),
          customer_photo_url: null,  // dropped from this flow — marketing pulls stills from videos
          consent,
        },
      });
      onDone?.();
    } catch (e) {
      setErr(e.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <>
      <Stepper stage="submit" />
      <div className="vt-submit-page">
        {/* Header — personalized, sets expectation */}
        <div className="animate-fade-in-up" style={{ marginBottom: 22 }}>
          <div className="vt-eyebrow"><span className="vt-bar"></span>You're almost there</div>
          <h1 className="vt-h-screen vt-submit-headline">Ready to send your videos, {firstName}?</h1>
          <p className="vt-lead vt-submit-lede">
            Have a quick look at what you're sending. When you press the button, we'll start the review.
          </p>
        </div>

        {/* Summary card — clip strip + email/payout rows */}
        <div className="vt-paper vt-submit-summary animate-fade-in-up" style={{ animationDelay: '80ms' }}>
          <div className="vt-submit-summary-head">
            <span className="vt-eyebrow" style={{ margin: 0 }}>
              <span className="vt-bar"></span>What you're sending
            </span>
          </div>

          <div className="vt-submit-clips">
            {recordedScripts.length > 0 ? recordedScripts.map((s, i) => {
              const c = clipForScript(s.key);
              const canPlay = !!c?.iframe_url;
              return (
                <button
                  type="button"
                  key={s.key + i}
                  className={'vt-submit-clip' + (canPlay ? ' vt-submit-clip-playable' : '')}
                  onClick={() => canPlay && setPlayingClip(c)}
                  disabled={!canPlay}
                  aria-label={canPlay ? `Play ${s.title}` : `${s.title} — polishing`}
                >
                  <div className="vt-submit-clip-thumb">
                    {c?.thumbnail_url ? (
                      <img
                        className="vt-submit-clip-poster"
                        src={c.thumbnail_url}
                        alt=""
                        loading="lazy"
                        /* If Stream CDN has a transient 404 right after
                           encoding finishes, hide the broken-image icon
                           and let the silhouette show through. Next poll
                           swap will retry. */
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <>
                        <div className="vt-submit-clip-face"></div>
                        <div className="vt-submit-clip-shoulders"></div>
                      </>
                    )}
                    <span className="vt-submit-clip-play">
                      {canPlay ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M7 4l13 8-13 8z"/>
                        </svg>
                      ) : (
                        /* Spinner while Stream is still encoding. Mirrors
                           the spinners on Between hero + ThankYou tile so
                           the customer gets consistent visual language. */
                        <svg width="14" height="14" viewBox="0 0 50 50" aria-hidden="true">
                          <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" strokeWidth="4" strokeOpacity=".25"/>
                          <path fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" d="M25 5 a20 20 0 0 1 20 20">
                            <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.9s" repeatCount="indefinite"/>
                          </path>
                        </svg>
                      )}
                    </span>
                    <span className="vt-submit-clip-num">{i + 1}</span>
                  </div>
                  <div className="vt-submit-clip-meta">
                    <span className="vt-submit-clip-title">{s.title}</span>
                    <span className="vt-submit-clip-len">{durationFor(s.key)}</span>
                  </div>
                </button>
              );
            }) : (
              <div className="vt-muted">No clips yet — head back and record at least one.</div>
            )}
          </div>

          <div className="vt-submit-divider" aria-hidden="true"></div>

          {/* Email + payout rows. Layout is "label / value" with the
              email getting an inline 'Change' button. Payout is a plain
              read-only display — no edit, the math is fixed by data. */}
          <div className="vt-submit-rows">
            <div className="vt-submit-row">
              <div className="vt-submit-row-label">
                <span className="vt-submit-row-ico" style={{ ['--vt-ico']: 'var(--ravepilot-orange)' }}>☺</span>
                Your name
              </div>
              {!editingName ? (
                <div className="vt-submit-row-value">
                  <span className="vt-confirm-value">{name || <em style={{ color: '#999', fontStyle: 'normal' }}>Add your name</em>}</span>
                  <button type="button" className="vt-confirm-edit vt-tap" onClick={() => setEditingName(true)}>
                    Change
                  </button>
                </div>
              ) : (
                <div className="vt-submit-row-edit">
                  <input
                    className="vt-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    autoFocus
                  />
                  <button type="button" className="vt-btn vt-btn-secondary vt-tap" onClick={() => setEditingName(false)}>
                    Done
                  </button>
                </div>
              )}
            </div>

            <div className="vt-submit-row">
              <div className="vt-submit-row-label">
                <span className="vt-submit-row-ico" style={{ ['--vt-ico']: 'var(--ravepilot-orange)' }}>✉</span>
                {rw.destLabel}
              </div>
              <div className="vt-submit-row-value">
                <span className="vt-confirm-value">{email}</span>
                <span className="vt-confirm-locked" title="From your invitation" aria-label="locked">🔒</span>
              </div>
            </div>

            <div className="vt-submit-row vt-submit-row-payout">
              <div className="vt-submit-row-label">
                <span className="vt-submit-row-ico" style={{ ['--vt-ico']: '#1A7A4D' }}>✦</span>
                Your reward
              </div>
              <div className="vt-submit-row-value vt-payout-value">
                <span className="vt-payout-base">
                  <strong>{rw.submitStrong}</strong>
                  <span className="vt-payout-meta">
                    once we accept {clipCount === 1 ? 'your clip' : 'a clip'}
                  </span>
                </span>
                <span className="vt-payout-bonus">
                  {rw.submitBonus}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Consent — same quiet checkbox + details disclosure as before.
            SYNC NOTE: the agreement text inside <details> is mirrored in
            the worker (src/index.js LEGAL_CONSENT_TEMPLATE, version
            v3-2026-05-28). If you edit this copy, edit there in the
            same commit. The worker renders its canonical version with
            the same dollar amounts and stores it (plus hash + version
            label + IP + UA + timestamp) on the submission row as
            evidence of what the customer agreed to. Drift between the
            two would mean we'd be storing different text than the
            customer actually saw. Paragraph structure (4 <p> tags)
            matches the worker template's \n\n separators. */}
        <label className="vt-consent-quiet animate-fade-in-up" style={{ animationDelay: '160ms' }}>
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span>
            {`I'm 18+ and I give ${rw.name} permission to use these videos in advertising.`}{' '}
            <details className="vt-consent-more">
              <summary>Read the full agreement</summary>
              <p>
                {`I give ${rw.legalName} (and its agents, contractors, advertising partners,
                successors, and anyone ${rw.name} transfers this license to) the permanent,
                irrevocable, worldwide, royalty-free, sublicensable right to use my name
                (including a shortened, first-name-only, or otherwise abbreviated form),
                my city or other location detail I provide, my likeness, my voice, and
                my recorded statements in advertising, social media, the ${rw.name} website,
                email, and any other media now or in the future. ${rw.name} may edit my video
                for length, clarity, or fit as long as the meaning of my statement is not
                changed.`}
              </p>
              <p>
                {`I represent that my statements are my honest opinion and reflect my actual
                experience with ${rw.name}. I will not knowingly say anything false, defamatory,
                or that infringes on someone else's rights. If a third party brings a claim
                against ${rw.name} because of something I said, I will cooperate in defending it.`}
              </p>
              <p>
                {`I understand ${rw.name} will disclose that I received compensation (${rw.ftc}) when this video is used in ads, as required by
                FTC guidelines. ${rw.name} may also process my video using automated systems
                (including AI) for quality review, transcription, and product improvement.`}
              </p>
              <p>
                {`I understand my compensation for this license is `}<strong>{rw.ftc}</strong>{`, provided after a video is accepted. This is my full and only compensation for this license. I waive the right to inspect or approve the final ads.`}
              </p>
            </details>
          </span>
        </label>

        {err && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded p-4 mt-4 text-base md:text-lg">
            {err}
          </div>
        )}

        {/* Submit + back row. Back stays in the bottom-left corner;
            the primary submit takes most of the visual weight. */}
        <div className="vt-actions vt-submit-actions animate-fade-in-up" style={{ animationDelay: '240ms' }}>
          <button className="vt-btn vt-btn-secondary vt-tap" onClick={onBack}>← Back</button>
          <span className="vt-grow"></span>
          <button
            className="vt-btn vt-btn-primary vt-btn-lg vt-tap vt-submit-primary"
            disabled={!name.trim() || !email.trim() || !consent || submitting}
            onClick={submit}
          >
            {submitting ? (
              <>
                <span className="vt-submit-spinner" aria-hidden="true"></span>
                Sending…
              </>
            ) : 'Send my videos →'}
          </button>
        </div>

        {/* Quiet "what happens next" footer — reassurance without anxiety.
            Same 3 steps used on the ThankYou page so the message is
            consistent end-to-end. */}
        <div className="vt-next-steps animate-fade-in-up" style={{ animationDelay: '320ms' }}>
          <div className="vt-next-steps-title">What happens next</div>
          <div className="vt-next-steps-row">
            <div className="vt-next-step">
              <span className="vt-next-step-num">1</span>
              <div className="vt-next-step-text">We watch each clip within 2 business days.</div>
            </div>
            <div className="vt-next-step">
              <span className="vt-next-step-num">2</span>
              <div className="vt-next-step-text">{rw.submitNextReward}</div>
            </div>
            <div className="vt-next-step">
              <span className="vt-next-step-num">3</span>
              <div className="vt-next-step-text">If we feature one in an ad, we will let you know.</div>
            </div>
          </div>
        </div>
      </div>

      {playingClip && (
        <ClipPlayerOverlay
          clip={playingClip}
          script={scripts.find((s) => s.key === playingClip.script_key)}
          onClose={() => setPlayingClip(null)}
        />
      )}
    </>
  );
}
